import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { PageHeader } from '../components/PageHeader.js';
import { api, type ShareDrop } from '../lib/api.js';

function fmtBytes(n: number): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ShareDetailPage({ shareId }: { shareId: string }) {
  usePageTitle('Share');
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetSession, setTargetSession] = useState('');

  const detail = useQuery({ queryKey: ['share', shareId], queryFn: () => api.getShare(shareId), refetchInterval: 10_000 });
  const contents = useQuery({ queryKey: ['share', shareId, 'contents'], queryFn: () => api.getShareContents(shareId), refetchInterval: 10_000 });
  const share = detail.data?.share;
  const sessions = useQuery({ queryKey: ['sessions', share?.buildId], queryFn: () => api.listSessions(share!.buildId), enabled: !!share?.buildId && !share?.sessionId });

  const revoke = useMutation({
    mutationFn: () => api.revokeShare(shareId),
    onSuccess: () => { toast.success('Share revoked'); void qc.invalidateQueries({ queryKey: ['share', shareId] }); void qc.invalidateQueries({ queryKey: ['shares'] }); },
    onError: (e) => toast.error(`Revoke failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const ingest = useMutation({
    mutationFn: () => api.ingestShareDrops(shareId, { dropIds: [...selected], targetSessionId: targetSession || undefined }),
    onSuccess: (res) => {
      toast.success(`Ingested ${res.ingested.length} file(s)${res.failed.length ? `, ${res.failed.length} failed` : ''}`);
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['share', shareId] });
    },
    onError: (e) => toast.error(`Ingest failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const portalUrl = share?.portalUrl ?? (share?.token ? `${window.location.origin}/portal.html?token=${share.token}` : null);
  const drops: ShareDrop[] = contents.data?.drops ?? [];
  const completeIds = drops.filter((d) => d.status === 'complete').map((d) => d.id);
  const canIngest = selected.size > 0 && (!!share?.sessionId || !!targetSession);

  function toggle(id: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader
        eyebrow="Workspace"
        title={share?.label || 'Share'}
        actions={
          <>
            <Link to="/shares" className="text-xs text-text-secondary hover:underline mr-2">← All shares</Link>
            {share && share.status === 'active' && (
              <button type="button" onClick={() => revoke.mutate()} disabled={revoke.isPending}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold" style={{ background: 'var(--accent-red)1f', color: 'var(--accent-red)', border: '1px solid var(--accent-red)55' }}>
                Revoke
              </button>
            )}
          </>
        }
      />
      <div className="flex-1 overflow-y-auto p-6 space-y-6 max-w-5xl">
        {detail.isLoading && <div className="text-sm text-text-dimmed">Loading…</div>}
        {share && (
          <>
            {/* Link + meta */}
            <div className="glass rounded-2xl p-5 space-y-3" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
              <div className="grid gap-3 text-sm" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                <Meta label="Build" value={<Link to="/builds/$id" params={{ id: share.buildId }} className="hover:underline" style={{ color: 'var(--accent-blue)' }}>{share.buildDisplayName ?? share.namespace}</Link>} />
                <Meta label="Linked session" value={share.sessionId ? <Link to="/sessions/$id" params={{ id: share.sessionId }} className="hover:underline" style={{ color: 'var(--accent-blue)' }}>{share.sessionId.slice(0, 8)}</Link> : <span className="text-text-dimmed">none</span>} />
                <Meta label="Email lock" value={share.lockedEmail ? `🔒 ${share.lockedEmail}` : <span className="text-text-dimmed">open (drop-only)</span>} />
                <Meta label="Status" value={share.status} />
                <Meta label="Expires" value={share.expiresAt ? fmtDate(share.expiresAt) : 'never'} />
                <Meta label="Created" value={`${fmtDate(share.createdAt)} · ${share.createdBy}`} />
              </div>
              {portalUrl && share.status === 'active' && (
                <div className="flex gap-2 items-center pt-1">
                  <input readOnly value={portalUrl} onFocus={(e) => e.currentTarget.select()}
                    className="flex-1 rounded-lg px-3 py-2 text-sm font-mono" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }} />
                  <button type="button" onClick={() => { void navigator.clipboard.writeText(portalUrl); toast.success('Copied'); }}
                    className="rounded-lg px-3 py-2 text-xs font-semibold" style={{ background: 'var(--accent-blue)1f', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)55' }}>Copy link</button>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
              <Stat label="Files dropped" value={String(detail.data?.stats.totalDrops ?? 0)} />
              <Stat label="Completed" value={String(detail.data?.stats.completeDrops ?? 0)} />
              <Stat label="Total size" value={fmtBytes(detail.data?.stats.totalBytes ?? 0)} />
            </div>

            {/* Contents + ingest */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">Dropped files</div>
                <div className="flex items-center gap-2">
                  {!share.sessionId && completeIds.length > 0 && (
                    <select value={targetSession} onChange={(e) => setTargetSession(e.target.value)} className="rounded-lg px-2 py-1 text-xs" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                      <option value="">Ingest into session…</option>
                      {sessions.data?.sessions.map((s) => <option key={s.id} value={s.id}>{s.title || s.id.slice(0, 8)}</option>)}
                    </select>
                  )}
                  <button type="button" disabled={!canIngest || ingest.isPending} onClick={() => ingest.mutate()}
                    className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-40" style={{ background: 'var(--accent-green)1f', color: 'var(--accent-green)', border: '1px solid var(--accent-green)55' }}
                    title={share.sessionId ? 'Ingest into the linked session' : 'Pick a target session first'}>
                    {ingest.isPending ? 'Ingesting…' : `Ingest selected (${selected.size})`}
                  </button>
                </div>
              </div>
              {drops.length === 0 ? (
                <div className="glass rounded-2xl p-6 text-center text-text-dimmed text-sm">No files dropped yet.</div>
              ) : (
                <div className="glass rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
                  <table className="w-full text-sm">
                    <tbody>
                      {drops.map((d) => (
                        <tr key={d.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td className="px-3 py-2 w-8">
                            <input type="checkbox" disabled={d.status !== 'complete'} checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                          </td>
                          <td className="px-2 py-2 text-text-primary">{d.filename}</td>
                          <td className="px-3 py-2 text-right font-mono text-text-secondary">{fmtBytes(d.sizeBytes)}</td>
                          <td className="px-3 py-2 text-[12px]" style={{ color: d.status === 'complete' ? 'var(--accent-green)' : d.status === 'failed' ? 'var(--accent-red)' : 'var(--accent-yellow)' }}>{d.status}</td>
                          <td className="px-3 py-2 text-[12px] text-text-dimmed">{d.verifiedEmail ?? ''}</td>
                          <td className="px-3 py-2 text-[12px] text-text-dimmed">{fmtDate(d.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-text-dimmed">{label}</div>
      <div className="text-text-primary">{value}</div>
    </div>
  );
}
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="glass rounded-xl p-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
      <div className="text-2xl font-bold text-text-primary">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-text-dimmed mt-1">{label}</div>
    </div>
  );
}
